import { Fragment, type ReactNode } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import type { SectionSpecItem } from "@/shared/models/SectionSpec";
import { formatM1SpecMeasurement } from "../utils/specUnits";

interface DimensionRow {
	code: string;
	label: string;
	item?: SectionSpecItem;
	entered: ReactNode;
	angle?: boolean;
	check?: boolean;
	/** Rendered after the row; usually a MeasurementErrorRow that watches its own value. */
	error?: ReactNode;
}

interface DimensionGroup {
	label?: string;
	rows: DimensionRow[];
}

const limit = (
	value: number | null | undefined,
	code: string,
	item?: SectionSpecItem,
) => formatM1SpecMeasurement(value, item ?? { Code: code }, "—");

export const MeasurementErrorRow = ({
	code,
	id,
	message,
}: {
	code: string;
	id: string;
	message: string;
}) => (
	<TableRow data-measurement-error={code}>
		<TableCell colSpan={5} id={id}>
			{message}
		</TableCell>
	</TableRow>
);

/** A single header and column grid keep every dimension and check aligned. */
export const InspectionDimensionTable = ({
	label,
	groups,
}: {
	label: string;
	groups: DimensionGroup[];
}) => (
	<TableContainer sx={{ minWidth: 0 }}>
		<Table
			size="small"
			aria-label={label}
			sx={{
				tableLayout: "fixed",
				"& tr, & th, & td": {
					px: 0.25,
					py: 0,
					height: 20,
					boxSizing: "border-box",
					fontSize: "0.75rem",
					lineHeight: "18px",
					whiteSpace: "nowrap",
				},
				"& td": { fontFamily: "monospace", fontVariantNumeric: "tabular-nums" },
				// Outrank both this table's nowrap rule and the form's doubled-class density rule.
				"&&& tr[data-measurement-error], &&& tr[data-measurement-error] > td": {
					height: "auto",
					whiteSpace: "normal",
					overflowWrap: "anywhere",
				},
				"&&& tr[data-measurement-error] > td": {
					py: 0.25,
					px: 0.5,
					color: "error.main",
					fontFamily: "inherit",
					lineHeight: 1.4,
				},
				"& tbody tr:last-child > *": { borderBottom: 0 },
			}}
		>
			<TableHead sx={{ backgroundColor: "action.hover" }}>
				<TableRow>
					{["Dim", "Min", "Target", "Entered", "Max"].map((heading, index) => (
						<TableCell
							key={heading}
							scope="col"
							align={index ? "center" : "left"}
							sx={{
								width: ["16%", "17%", "17%", "33%", "17%"][index],
								color: "text.secondary",
								fontWeight: 500,
							}}
						>
							{heading}
						</TableCell>
					))}
				</TableRow>
			</TableHead>
			<TableBody>
				{groups.map((group, index) => (
					<Fragment key={group.label ?? index}>
						{group.label && (
							<TableRow sx={{ backgroundColor: "action.hover" }}>
								<TableCell
									component="th"
									scope="rowgroup"
									colSpan={5}
									sx={{ fontWeight: 600 }}
								>
									{group.label}
								</TableCell>
							</TableRow>
						)}
						{group.rows.map((row) => {
							const item = row.item?.Applies ? row.item : undefined;
							return (
								<Fragment key={row.code}>
									<TableRow
										sx={{
											color: item ? "text.primary" : "text.disabled",
											"&& > *": { color: "inherit" },
										}}
									>
										<TableCell
											component="th"
											scope="row"
											colSpan={row.check ? 3 : 1}
											title={row.item?.Name}
										>
											{row.item?.DisplayName ?? row.label}
										</TableCell>
										{!row.check && (
											<>
												<TableCell align="center">
													{limit(item?.Ltl, row.code, item)}
												</TableCell>
												<TableCell align="center">
													{limit(item?.Nominal, row.code, item)}
												</TableCell>
											</>
										)}
										<TableCell align="center">{row.entered}</TableCell>
										<TableCell align="center">
											{row.check ? null : limit(item?.Utl, row.code, item)}
										</TableCell>
									</TableRow>
									{row.error}
								</Fragment>
							);
						})}
					</Fragment>
				))}
			</TableBody>
		</Table>
	</TableContainer>
);
